import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Star, PenLine, Quote } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-white border border-parchborder rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-dill transition-colors";

const StarRow = ({ value, onPick }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((n) =>
      onPick ? (
        <button
          key={n}
          type="button"
          onClick={() => onPick(n)}
          className="transition-transform hover:scale-125"
          aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
          data-testid={`review-star-pick-${n}`}
        >
          <Star className={`w-7 h-7 ${n <= value ? "fill-gold text-gold" : "text-ink/20"}`} />
        </button>
      ) : (
        <Star key={n} className={`w-4 h-4 ${n <= value ? "fill-gold text-gold" : "text-ink/15"}`} />
      )
    )}
  </div>
);

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("review") === "1") {
      setShowForm(true);
      const n = params.get("name");
      if (n) setName(n.slice(0, 60));
      setTimeout(() => {
        const el = document.getElementById("reviews");
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
        else el.scrollIntoView({ behavior: "smooth" });
      }, 350);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reviews", { name, rating, text });
      toast.success("Thanks! Your review will appear here once the owner approves it.");
      setName("");
      setRating(5);
      setText("");
      setShowForm(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="reviews" className="py-12 sm:py-16" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-wrap items-end justify-between gap-4 mb-10"
        >
          <div className="max-w-2xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terra font-semibold mb-3">
              Word from the Porch
            </p>
            <h2 className="font-serif font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-dill leading-tight">
              What the neighbors are saying.
            </h2>
          </div>
          <button
            onClick={() => setShowForm((s) => !s)}
            className="flex items-center gap-2 bg-terra hover:bg-terra-hover text-parchment font-semibold rounded-full px-5 py-2.5 text-sm transition-colors"
            data-testid="review-leave-button"
          >
            <PenLine className="w-4 h-4" /> Leave a Review
          </button>
        </motion.div>

        {reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <motion.figure
                key={r.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="bg-white border border-parchborder rounded-2xl p-6 shadow-sm flex flex-col"
                data-testid={`review-card-${r.id}`}
              >
                <Quote className="w-6 h-6 text-terra/30 mb-3" />
                <blockquote className="font-serif italic text-ink/80 leading-relaxed flex-1">
                  “{r.text}”
                </blockquote>
                <figcaption className="mt-5 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-bold text-sm text-dill">{r.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-ink/40">
                      {new Date(r.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <StarRow value={r.rating} />
                </figcaption>
              </motion.figure>
            ))}
          </div>
        ) : (
          !showForm && (
            <div className="text-center py-14 bg-white rounded-2xl border border-parchborder" data-testid="reviews-empty">
              <Star className="w-8 h-8 text-gold/40 mx-auto mb-3" />
              <p className="font-serif text-xl text-dill/60 italic">No reviews yet.</p>
              <p className="text-sm text-ink/50 mt-1">Be the first to rave about the snap.</p>
            </div>
          )
        )}

        {showForm && (
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className={`${reviews.length > 0 ? "mt-8" : ""} max-w-xl mx-auto bg-white border border-parchborder rounded-2xl p-6 sm:p-8 shadow-xl`}
            data-testid="review-form"
          >
            <h3 className="font-serif font-bold text-xl text-dill mb-1">Leave a review</h3>
            <p className="text-xs text-ink/50 mb-6">
              Tell the neighborhood how the crunch landed. Reviews appear after the owner gives them a nod.
            </p>
            <div className="space-y-4">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 block mb-1.5">Your name *</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={60}
                  className={inputCls}
                  data-testid="review-name-input"
                />
              </label>
              <div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 block mb-1.5">Your rating *</span>
                <StarRow value={rating} onPick={setRating} />
              </div>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 block mb-1.5">Your review *</span>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  required
                  rows={4}
                  maxLength={600}
                  placeholder="Crunch, tang, heat — how was the jar?"
                  className={inputCls}
                  data-testid="review-text-input"
                />
              </label>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <button
                type="submit"
                disabled={busy || !name.trim() || !text.trim()}
                className="bg-dill hover:bg-terra disabled:opacity-50 text-parchment font-bold rounded-full px-6 py-2.5 text-sm transition-colors flex items-center gap-2"
                data-testid="review-submit-button"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Post Review
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-sm text-ink/50 hover:text-terra transition-colors"
                data-testid="review-cancel-button"
              >
                Cancel
              </button>
            </div>
          </motion.form>
        )}
      </div>
    </section>
  );
};
